import React, { useEffect } from "react";
import { Target, Eye, Flag, CheckCircle2 } from "lucide-react";
import { IMAGES, LEADERSHIP, ORG } from "../mock";

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div>
      <section className="hero-pattern text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="font-serif text-3xl sm:text-5xl font-bold mb-4">About the Commission</h1>
          <p className="text-slate-200 max-w-2xl text-lg">{ORG.tagline}</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-sm font-semibold text-brand-green uppercase tracking-wide">Who we are</span>
            <h2 className="font-serif text-3xl font-bold text-brand-navy mt-3 mb-5">An independent statutory regulator</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              The {ORG.name} ({ORG.shortName}) is the apex regulator of the capital market in Sierra Leone.
              Established under the Securities Industry Act, the Commission is mandated to regulate the
              issuance, sale and trading of securities, and to license and supervise market intermediaries.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our work underpins investor confidence and the orderly development of a market that channels
              savings into productive investment across the Republic of Sierra Leone.
            </p>
          </div>
          <img src={IMAGES.building} alt="Commission headquarters" className="rounded-2xl shadow-xl w-full h-[380px] object-cover" />
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {[
            { Icon: Target, title: "Our Mission", text: "To protect investors and promote fair, efficient and transparent securities markets that support economic growth." },
            { Icon: Eye, title: "Our Vision", text: "A trusted, vibrant and inclusive capital market that mobilises domestic and diaspora capital for national development." },
            { Icon: Flag, title: "Our Values", text: "Integrity, independence, accountability, fairness and professionalism in everything we do." },
          ].map((c) => (
            <div key={c.title} className="bg-white rounded-xl border border-slate-200 p-7">
              <div className="w-12 h-12 rounded-lg bg-[#eaf4ee] flex items-center justify-center mb-4">
                <c.Icon className="text-brand-green" size={24} />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-navy mb-2">{c.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <img src={IMAGES.education} alt="Investor education" className="rounded-2xl shadow-xl w-full h-[360px] object-cover order-2 lg:order-1" />
          <div className="order-1 lg:order-2">
            <span className="text-sm font-semibold text-brand-green uppercase tracking-wide">Statutory functions</span>
            <h2 className="font-serif text-3xl font-bold text-brand-navy mt-3 mb-5">Our core responsibilities</h2>
            <ul className="space-y-3">
              {[
                "License securities exchanges, brokers, dealers and advisers",
                "Approve prospectuses and public offers of securities",
                "Supervise market conduct and prudential compliance",
                "Investigate and sanction market misconduct",
                "Promote investor education and market development",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-slate-700">
                  <CheckCircle2 size={20} className="text-brand-green shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-brand-navy mb-3">Leadership</h2>
            <div className="section-divider mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {LEADERSHIP.map((p) => (
              <div key={p.name} className="bg-white rounded-xl border border-slate-200 p-6 text-center">
                <div className="w-20 h-20 rounded-full bg-brand-navy text-white font-serif text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {p.initials}
                </div>
                <h3 className="font-semibold text-brand-navy">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
